import {
    areAuditViewsHidden,
    DEFAULT_AUDIT_LOG_FILTERS,
    normalizeAuditLogFilters,
} from './filterRules';
import type { AuditLogFilterKey, AuditLogFilters } from './types';
import { AUDIT_LOG_FILTER_KEYS } from './types';

/**
 * What a click on a value asks for. `add` replaces the filters it names and keeps the
 * others. `replace` drops every filter, as the request pill and the time pivot do, but
 * keeps whether audit log views are hidden: that is how the reader looks at the log,
 * not part of the question the pivot asks, so hidden views stay hidden and shown ones
 * stay shown.
 */
export interface AuditFilterChange {
    mode: 'add' | 'replace';
    filters: AuditLogFilters;
}

// Keys that only mean something together. Setting one clears the rest, so a click never
// pairs a new id with an old type, and a scope click replaces whichever level is set.
const LINKED_KEYS: ReadonlyArray<readonly AuditLogFilterKey[]> = [
    ['actorType', 'actorUserId'],
    ['entityType', 'entityId'],
    ['clientId', 'locationId', 'cageId'],
    ['changedField', 'changedFrom', 'changedTo'],
    ['metadataPath', 'metadataValue'],
];

export function addAuditFilters(filters: AuditLogFilters): AuditFilterChange {
    return { mode: 'add', filters };
}

export function replaceAuditFilters(filters: AuditLogFilters): AuditFilterChange {
    return { mode: 'replace', filters };
}

export function applyAuditFilterChange(
    current: AuditLogFilters,
    change: AuditFilterChange
): AuditLogFilters {
    if (change.mode === 'replace') {
        const base = areAuditViewsHidden(current) ? DEFAULT_AUDIT_LOG_FILTERS : {};
        return normalizeAuditLogFilters({ ...base, ...change.filters });
    }
    const touched = AUDIT_LOG_FILTER_KEYS.filter((key) => change.filters[key] !== undefined);
    const next: AuditLogFilters = { ...current };
    for (const group of LINKED_KEYS) {
        if (group.some((key) => touched.includes(key))) {
            for (const key of group) delete next[key];
        }
    }
    return normalizeAuditLogFilters({ ...next, ...change.filters });
}
